// Loadout inventory + pre-order helpers.
// Ground-truth signal is `variant.quantityAvailable` (numeric).  We ignore
// `availableForSale` because Shopify's inventory policy 'continue' makes it
// true even when the SKU is at 0 — the storefront should NOT advertise
// backorders as "IN STOCK".

export const WINDOW_DAYS_PREORDER = { earliest: 16, latest: 24 };
export const WINDOW_DAYS_INSTOCK = 4;

// ---- Canonical dispatch strings (HYPHEN-MINUS in both places, per spec) ----
const IN_STOCK_LINE = "SHIPS IN 2-4 BUSINESS DAYS";

const fmtDate = (d) => {
  try {
    return d
      .toLocaleDateString("en-GB", { day: "2-digit", month: "short" })
      .toUpperCase();
  } catch {
    const months = ["JAN","FEB","MAR","APR","MAY","JUN","JUL","AUG","SEP","OCT","NOV","DEC"];
    const dd = String(d.getDate()).padStart(2, "0");
    return `${dd} ${months[d.getMonth()]}`;
  }
};

// -------------------------------------------------------------
// Inventory
// -------------------------------------------------------------
// Sum `quantityAvailable` across every variant.  If the field is null
// everywhere (token lacks the scope), or the field is missing, we treat the
// product as PRE-ORDER — this is the safe-by-default mode.
export function isInStock(product) {
  if (!product) return false;
  const edges = product?.variants?.edges;
  if (Array.isArray(edges) && edges.length > 0) {
    // In stock if ANY variant has qty > 0 — oversold (negative) variants
    // must not cancel out positive ones.
    let sawNumber = false;
    for (const e of edges) {
      const q = e?.node?.quantityAvailable;
      if (typeof q === "number") {
        sawNumber = true;
        if (q > 0) return true;
      }
    }
    if (sawNumber) return false;
    // Every variant returned null (scope not enabled) → pre-order.
    return false;
  }
  // Legacy `firstVariant` shape (only exposed by earlier callers).
  const q = product?.firstVariant?.quantityAvailable;
  if (typeof q === "number") return q > 0;
  return false;
}

// Sold out = Shopify says not purchasable (continue-selling off, qty ≤ 0).
export function isSoldOut(product) {
  return product?.availableForSale === false && !isInStock(product);
}

export function statusChipFor(product) {
  return isInStock(product) ? "IN STOCK" : "PRE-ORDER";
}

// -------------------------------------------------------------
// Dispatch window
// -------------------------------------------------------------
export function getPreorderWindow(today = new Date()) {
  return {
    start: new Date(today.getTime() + WINDOW_DAYS_PREORDER.earliest * 86400000),
    end: new Date(today.getTime() + WINDOW_DAYS_PREORDER.latest * 86400000),
  };
}

// status: "in-stock" | "pre-order"  (accepts legacy "IN_STOCK" / "PRE_ORDER" too)
export function formatDispatchRange(today = new Date(), status = "in-stock") {
  const key = String(status).toLowerCase().replace("_", "-");
  if (key === "in-stock") return IN_STOCK_LINE;
  const { start, end } = getPreorderWindow(today);
  return `ARRIVES ${fmtDate(start)} - ${fmtDate(end)}`;
}

// -------------------------------------------------------------
// Legacy Prompt-1 API — still used by homepage BestsellersGrid and
// components/loadout/ProductCard.  Kept for backwards compat; delegates
// to the new signal so the chip logic is now inventory-aware everywhere.
// -------------------------------------------------------------
export function getProductAvailability(variantOrProduct, productTags = [], now = new Date()) {
  // eslint-disable-next-line no-unused-vars
  const _tagsReservedForFutureUse = productTags;

  // Accept either a full product (with .variants.edges or .firstVariant)
  // or a bare variant object.  Normalise into a product-shaped object.
  let product;
  if (variantOrProduct && variantOrProduct.variants) {
    product = variantOrProduct;
  } else if (variantOrProduct && (variantOrProduct.quantityAvailable !== undefined || variantOrProduct.availableForSale !== undefined)) {
    product = { firstVariant: variantOrProduct };
  } else {
    product = variantOrProduct || null;
  }

  const inStock = isInStock(product);

  if (!inStock) {
    const { start, end } = getPreorderWindow(now);
    return {
      status: "PRE_ORDER",
      chipLabel: "PRE-ORDER",
      dispatchLine: `Estimated dispatch: ${WINDOW_DAYS_PREORDER.earliest}-${WINDOW_DAYS_PREORDER.latest} days (${fmtDate(start)} - ${fmtDate(end)}).`,
      dispatchRange: `${fmtDate(start)} - ${fmtDate(end)}`,
      earliestDate: start,
      latestDate: end,
      ctaLabel: "RESERVE LOADOUT",
      ctaMicrocopy: "Deposit: 100% upfront via Razorpay. No charge until dispatch.",
    };
  }
  return {
    status: "IN_STOCK",
    chipLabel: "IN STOCK",
    dispatchLine: "Ships in 2-4 business days from order date.",
    dispatchRange: `SHIPS IN 2-4 BUSINESS DAYS`,
    earliestDate: now,
    latestDate: new Date(now.getTime() + WINDOW_DAYS_INSTOCK * 86400000),
    ctaLabel: "ADD TO CART",
  };
}
